import { useMemo, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import {
  PROVIDERS,
  providerBlurb,
  tierLabel,
  type Duration,
  type Resolution,
} from '@/lib/editorCapabilities';
import type { Video2dProviderId, VideoClipTransparency } from '@/services/avatarApi';
import { PromptInIngleseHint } from '@/components/avatars/PromptInIngleseHint';
import { friendlyAnimate2dError } from '@/components/avatars/animate2dErrors';

interface Props {
  open: boolean;
  isPending?: boolean;
  /** Server-side or transport error from the most recent submission attempt,
   *  shown as a visible banner so the user knows why "Generate" appeared to
   *  do nothing. */
  errorMessage?: string | null;
  onClose: () => void;
  onGenerate: (params: {
    prompt: string;
    provider: Video2dProviderId;
    durationSec: Duration;
    resolution: Resolution;
    variants: 1 | 2 | 3;
    transparency: VideoClipTransparency;
    seed?: number;
  }) => void;
}

const MAX_PROMPT_CHARS = 300;

/**
 * Phase 7a+ — 2D animation modal. The PROVIDERS capability matrix is now
 * shared with the Avatar Editor wizard via @/lib/editorCapabilities — one
 * source of truth for durations, resolutions, costs, and tier labels.
 */

export function Animate2dModal({ open, isPending, errorMessage, onClose, onGenerate }: Props) {
  const { t } = useTranslation('avatars');
  useLivelloIndietro(open, onClose);
  const haptics = useHaptics();
  const [providerId, setProviderId] = useState<Video2dProviderId>('hailuo-fal');
  const [prompt, setPrompt] = useState('');
  const [durationSec, setDurationSec] = useState<Duration>(6);
  // Default to 512p — minimum resolution for Hailuo (cheapest test runs). The
  // safeResolution memo clamps to the provider's lowest option if the user
  // switches to a provider that doesn't support 512p.
  const [resolution, setResolution] = useState<Resolution>('512p');
  const [variants, setVariants] = useState<1 | 2 | 3>(1);
  // Seed: empty string = "random" (omit from payload), parsed int otherwise.
  const [seedInput, setSeedInput] = useState<string>('');
  // Default to true alpha (matting) — it's the highest-quality transparency
  // mode and what users almost always want for avatar-style clips. The
  // chroma-key path remains available as a cheaper / always-on fallback.
  const [transparency, setTransparency] = useState<VideoClipTransparency>('matting');

  const provider = useMemo(() => PROVIDERS.find(p => p.id === providerId)!, [providerId]);

  // Clamp duration/resolution to whatever the chosen provider supports.
  const safeDuration: Duration = provider.durations.includes(durationSec) ? durationSec : provider.durations[0];
  const safeResolution: Resolution = provider.resolutions.includes(resolution) ? resolution : provider.resolutions[0];

  const trimmed = prompt.trim();
  const canGenerate = trimmed.length >= 3 && !isPending;

  // Live cost estimate — matches backend estimateCostCents per provider.
  // Matting transparency tacks on the veed/fal post-process bill (~$0.018/s),
  // so it must be included in the per-clip estimate for the button label.
  const estimateUsd = useMemo(() => {
    let base: number;
    if (provider.basePriceCents && provider.id === 'kling-fal') {
      const extra = Math.max(0, safeDuration - 5);
      base = (provider.basePriceCents + extra * 5) / 100;
    } else {
      const perSec = provider.costPerSecCents[safeResolution] ?? 4.5;
      base = (safeDuration * perSec) / 100;
    }
    const mattingAddOn = transparency === 'matting' ? safeDuration * 0.018 : 0;
    return base + mattingAddOn;
  }, [provider, safeDuration, safeResolution, transparency]);

  if (!open) return null;

  const handleGenerate = () => {
    if (!canGenerate) return;
    haptics.impact('medium');
    const seedNum = seedInput.trim().length > 0 ? Number(seedInput.trim()) : undefined;
    const seed = typeof seedNum === 'number' && Number.isFinite(seedNum) && Number.isInteger(seedNum)
      ? seedNum
      : undefined;
    onGenerate({
      prompt: trimmed,
      provider: providerId,
      durationSec: safeDuration,
      resolution: safeResolution,
      variants,
      transparency,
      seed,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">{t('animate2d.title')}</h1>
          <p className="mt-0.5 text-xs text-fg-muted">{t('animate2d.subtitle')}</p>
        </div>
        <Button
          variant="icona"
          size={36}
          tono="pieno"
          aria-label={t('common.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          {/* Provider picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.engine')}</label>
            <div className="grid grid-cols-2 gap-2">
              {PROVIDERS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { haptics.selection(); setProviderId(p.id); }}
                  className={cn(
                    'flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                    providerId === p.id
                      ? 'border-brand bg-brand/10 text-fg'
                      : 'border-border bg-bg-secondary text-fg',
                  )}
                >
                  <span className="flex w-full items-center justify-between">
                    <span className="font-bold">{p.label}</span>
                    <span className="rounded-full bg-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                      {tierLabel(t, p.tier)}
                    </span>
                  </span>
                  <span className="text-[10px] text-fg-muted">{providerBlurb(t, p.id)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Motion prompt */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="a2d-prompt">
              {t('animate2d.motionPrompt')}
            </label>
            <textarea
              id="a2d-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={MAX_PROMPT_CHARS}
              placeholder={t('animate2d.motionPlaceholder')}
              className="min-h-[120px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
            <PromptInIngleseHint />
            <p className="text-right text-[11px] text-fg-muted">{trimmed.length} / {MAX_PROMPT_CHARS}</p>
          </div>

          {/* Duration */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.duration')}</label>
            <div className="flex gap-2">
              {provider.durations.map((d) => (
                <Button
                  key={d}
                  variant="scelta"
                  forma="tessera"
                  tono="tenue"
                  pressed={safeDuration === d}
                  className="flex-1"
                  onClick={() => { haptics.selection(); setDurationSec(d); }}
                >
                  {d}s
                </Button>
              ))}
            </div>
          </div>

          {/* Background / transparency — Phase 7c.
              none       → opaque MP4 (default).
              chroma-key → prompt is mutated server-side to force a solid
                           magenta background; the client uses a canvas
                           color-key shader to make it transparent at render
                           time. Free, fast, faux-alpha.
              matting    → backend post-process to true WebM VP9 alpha.
                           ~+$0.10–0.20/clip + 15–25s extra. Real alpha but
                           pipeline ships separately (#185); selecting it
                           today behaves like chroma-key. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.background')}</label>
            <div className="flex gap-2">
              {([
                { id: 'none', label: t('transparency.none') },
                { id: 'chroma-key', label: t('transparency.chromaKey') },
                { id: 'matting', label: t('transparency.matting') },
              ] as const).map(opt => (
                <Button
                  key={opt.id}
                  variant="scelta"
                  forma="tessera"
                  tono="tenue"
                  pressed={transparency === opt.id}
                  className="flex-1 text-xs"
                  onClick={() => { haptics.selection(); setTransparency(opt.id); }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            {transparency === 'chroma-key' && (
              <p className="text-[11px] text-fg-muted">
                {t('animate2d.chromaKeyNote')}
              </p>
            )}
            {transparency === 'matting' && (
              <p className="text-[11px] text-fg-muted">
                {t('animate2d.mattingNote')}
              </p>
            )}
          </div>

          {/* Variants — generate N parallel clips with the same prompt.
              Each variant is billed separately; user gets N chances to find
              the best output without re-typing the prompt. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.variants')}</label>
            <div className="flex gap-2">
              {([1, 2, 3] as const).map((n) => (
                <Button
                  key={n}
                  variant="scelta"
                  forma="tessera"
                  tono="tenue"
                  pressed={variants === n}
                  className="flex-1"
                  onClick={() => { haptics.selection(); setVariants(n); }}
                >
                  {t('animate2d.variantOption', { count: n })}
                </Button>
              ))}
            </div>
            {variants > 1 && (
              <p className="text-[11px] text-fg-muted">
                {t('animate2d.variantsBilling', { n: variants })}
              </p>
            )}
          </div>

          {/* Seed — optional integer for reproducible runs. Empty = random
              (provider picks). Useful to A/B engines under matched noise. */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="a2d-seed" className="text-xs font-semibold uppercase text-fg-muted">
              {t('animate2d.seed')} <span className="font-normal normal-case text-fg-muted/60">{t('common.optional')}</span>
            </label>
            <input
              id="a2d-seed"
              type="number"
              inputMode="numeric"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              placeholder={t('animate2d.seedPlaceholder')}
              className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
          </div>

          {/* Resolution (skipped when provider only supports one) */}
          {provider.resolutions.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.resolution')}</label>
              <div className="flex gap-2">
                {provider.resolutions.map((r) => (
                  <Button
                    key={r}
                    variant="scelta"
                    forma="tessera"
                    tono="tenue"
                    pressed={safeResolution === r}
                    className="flex-1"
                    onClick={() => { haptics.selection(); setResolution(r); }}
                  >
                    {r}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Visible submission error banner */}
          {(() => {
            const err = friendlyAnimate2dError(errorMessage, t);
            if (!err) return null;
            return (
              <div role="alert" className="flex flex-col gap-1 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-fg">
                <p className="font-semibold">{err.title}</p>
                {err.detail && <p className="text-xs text-fg-muted">{err.detail}</p>}
              </div>
            );
          })()}

          {/* Tips */}
          <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
            <p className="font-semibold text-fg">{t('common.tips')}</p>
            <ul className="mt-1 space-y-0.5">
              <li>• {t('animate2d.tips.action')}</li>
              <li>• {t('animate2d.tips.adverb')}</li>
              <li>• {t('animate2d.tips.loop')}</li>
              <li>• <Trans t={t} i18nKey="animate2d.tips.engines" components={{ b: <strong /> }} /></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <Button fullWidth onClick={handleGenerate} disabled={!canGenerate}>
          {isPending ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Sparkles aria-hidden className="h-4 w-4" />}
          {t('animate2d.generate', { count: variants, provider: provider.label, cost: (estimateUsd * variants).toFixed(2) })}
        </Button>
      </div>
    </div>
  );
}
